import { Promise as BPromise } from 'bluebird'
import _ from 'lodash'

const ERROR_PROCESSING = 423
const ERROR_GENERIC = 400 // RequestSink.java has 2 codes for "concurrent modification detected" - 400 and 409...
const CONCURRENT_MODIFICATION = "Concurrent modification detected, please retry"
const TEMPORARY_ERROR_CODES = [ERROR_PROCESSING]

/**
 * Whether an error body from the Vector server indicates a concurrent modification.
 *
 * Some examples:
 *
 * {
 *   "statusCode": 400,
 *   "errors": [
 *     {
 *       "entityId": "a0bad950-2dbc-45ad-95e1-b09c3e80c70f",
 *       "field": "modifiedDate",
 *       "message": "Concurrent modification detected, please retry"
 *     }
 *   ]
 * }
 *
 * {
 *   "code": 400,
 *   "errors": {
 *     "modifiedDate": [
 *       "Concurrent modification detected, please retry"
 *     ]
 *   }
 * }
 *
 * Entity <UUID> cannot be modified while processing
 */
function isConcurrentModification(error: any): boolean {
  return (
    error === CONCURRENT_MODIFICATION ||
    error?.message === CONCURRENT_MODIFICATION ||
    error?.errors?.modifiedDate?.includes?.(CONCURRENT_MODIFICATION) ||
    error?.errors?.some?.((e: any) => e?.message === CONCURRENT_MODIFICATION)
  )
}

function checkRetryable(code: number, error: any): boolean {
  return (
    _.includes(TEMPORARY_ERROR_CODES, code) ||
    (code === ERROR_GENERIC && isConcurrentModification(error))
  )
}

export function retryOnError(promiseFn) {
  const maxRetries = 10
  let retries = 0

  return new BPromise((resolve, reject) => {
    const retryFunction = () => {
      promiseFn().then((entity) => {
        resolve(entity)
      }).catch((e) => {
        if (retries >= maxRetries || !isRetryable(e)) {
          return reject(e)
        }

        retries += 1
        BPromise.delay(500 * retries).then(retryFunction)
      })
    }

    retryFunction()
  })
}

export function isRetryable(error: any): boolean {
  return checkRetryable(error.status || error.code, error)
}

export function isXHRRetryable(request: XMLHttpRequest): boolean {
  let error: any
  try {
    error = JSON.parse(request.responseText)
  } catch {
    error = request.responseText
  }
  return checkRetryable(request.status, error)
}
